import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import Swal from 'sweetalert2';
import ViewKeyAllocatedDetail from '../Key Dashboard/ViewKeyAllocatedDetail';
import apiDefinitions from '../../../../core/api/apiDefinitions';

interface KeyAllocation {
  id: number;
  keyId: number;
  keyCode: string;
  handoverTo: string;
  returnedBy: string | null;
  handoverBy: string;
  reason: string | null;
  isRestricted: boolean;
  returnTo: string | null;
  status: string;
  handoverType: string | null;
  handoverDate: string;
  returnDate: string | null;
}

interface UserDTO {
  empNumber: string;
  name: string;
}

interface KeyAllocationTableProps {
  data: KeyAllocation[];
  statusColors: { [key: string]: string };
  handleViewClick: (id: number) => void;
  onMarkSuccess: () => void; // Callback to refetch data in parent
}

const KeyAllocationTable: React.FC<KeyAllocationTableProps> = ({
  data,
  statusColors,
  handleViewClick,
  onMarkSuccess,
}) => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState<KeyAllocation | null>(null);
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [handoverKey, setHandoverKey] = useState<KeyAllocation | null>(null);
  const [markStatus, setMarkStatus] = useState<'RETURNED' | 'LOST'>('RETURNED');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allocations, setAllocations] = useState<KeyAllocation[]>(data);

  // ===== Pagination (10 per page) =====
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((allocations?.length || 0) / PAGE_SIZE)),
    [allocations]
  );

  const paginatedAllocations = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return allocations.slice(start, end);
  }, [allocations, currentPage]);

  // Keep current page valid when data changes
  useEffect(() => {
    setAllocations(data);
  }, [data]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
    if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  const showingFrom = useMemo(
    () => (allocations.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1),
    [allocations.length, currentPage]
  );
  const showingTo = useMemo(
    () => Math.min(currentPage * PAGE_SIZE, allocations.length),
    [allocations.length, currentPage]
  );
  // ===== End Pagination =====

  // Fetch users for the dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const payload = { filterType: null, filterValue: '', date: null };
        const response = await apiDefinitions.getUsers(1, payload);
        if (response.data.status === 'S1000' && response.data.data.users) {
          const fetchedUsers = response.data.data.users
            .filter((user: any) => user.empNumber && user.name)
            .map((user: any) => ({
              empNumber: user.empNumber.toString(),
              name: user.name || 'Unknown',
            }));
          setUsers(fetchedUsers);
        } else {
          setError('Failed to fetch users: ' + response.data.statusDescription);
        }
      } catch (err: any) {
        setError(err.response?.data?.statusDescription || 'Failed to fetch users.');
      }
    };
    if (showHandoverModal) {
      fetchUsers();
    }
  }, [showHandoverModal]);

  // Filter users for dropdown
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    const s = searchTerm.toLowerCase();
    return users.filter(
      (u) => u.empNumber.toLowerCase().includes(s) || u.name.toLowerCase().includes(s)
    );
  }, [users, searchTerm]);

  // Define handover type colors
  const getHandoverTypeClass = (handoverType: string | null) => {
    switch (handoverType) {
      case 'TEMPORARY':
        return 'badge bg-success';
      case 'PERMANENT':
        return 'badge bg-secondary';
      default:
        return 'badge bg-secondary';
    }
  };

  const handleLocalViewClick = (allocation: KeyAllocation) => {
    setSelectedKey(allocation);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedKey(null);
  };

  const handleHandoverClick = (allocation: KeyAllocation) => {
    if (allocation.status !== 'HANDOVER') {
      setError('This key cannot be marked as it is not in HANDOVER status.');
      Swal.fire({
        icon: 'error',
        title: 'Invalid Status',
        text: 'This key cannot be marked as it is not in HANDOVER status.',
      });
      return;
    }
    setHandoverKey(allocation);
    setMarkStatus('RETURNED');
    setSearchTerm('');
    setSelectedUser('');
    setError(null);
    setShowHandoverModal(true);
  };

  const handleCloseHandoverModal = () => {
    setShowHandoverModal(false);
    setHandoverKey(null);
    setMarkStatus('RETURNED');
    setSearchTerm('');
    setSelectedUser('');
    setError(null);
  };

  const handleMarkReturned = async () => {
    if (!handoverKey) return;
    if (markStatus === 'RETURNED' && !selectedUser) {
      setError('Please select a user for RETURNED status.');
      return;
    }
    if (handoverKey.status !== 'HANDOVER') {
      setError('This key cannot be marked as it is not in HANDOVER status.');
      Swal.fire({
        icon: 'error',
        title: 'Invalid Status',
        text: 'This key cannot be marked as it is not in HANDOVER status.',
      });
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      const payload = {
        allocationIds: [handoverKey.id],
        status: markStatus,
        ...(markStatus === 'RETURNED' && { returnBy: selectedUser }),
      };
      const response = await apiDefinitions.markReturned(payload);
      if (response.data.status === 'S1000') {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Key marked as ${markStatus}.`,
        });
        onMarkSuccess(); // Trigger refetch in parent
        handleCloseHandoverModal();
      } else {
        setError(response.data.statusDescription || 'Failed to mark key status.');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response.data.statusDescription || 'Failed to mark key status.',
        });
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.statusDescription || 'Failed to mark key status.';
      setError(errorMsg);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (allocations.length === 0) {
    return <p className="text-muted">No data available for this category.</p>;
  }

  return (
    <>
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-primary">
            <tr>
              <th>ID</th>
              <th>Key Code</th>
              <th>Handover To</th>
              <th>Status</th>
              <th>Handover Type</th>
              <th>Returned By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedAllocations.map((allocation) => (
              <tr key={allocation.id}>
                <td>{allocation.id}</td>
                <td>{allocation.keyCode}</td>
                <td>{allocation.handoverTo}</td>
                <td>
                  <span className={statusColors[allocation.status] || 'badge bg-secondary'}>
                    {allocation.status}
                  </span>
                </td>
                <td>
                  <span className={getHandoverTypeClass(allocation.handoverType)}>
                    {allocation.handoverType || 'N/A'}
                  </span>
                </td>
                <td>{allocation.returnedBy || 'N/A'}</td>
                <td>
                  <div className="hstack gap-2 fs-3">
                    <button
                      type="button"
                      className="btn-icon btn-sm btn-soft-info rounded-pill"
                      onClick={() => {
                        handleViewClick(allocation.id);
                        handleLocalViewClick(allocation);
                      }}
                      title="View Key Details"
                    >
                      <i className="ti ti-eye" />
                    </button>
                    {allocation.status === 'HANDOVER' && (
                      <button
                        type="button"
                        className="btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                        onClick={() => handleHandoverClick(allocation)}
                        title="Mark Handover"
                      >
                        <i className="ti ti-check" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination footer */}
        <div className="d-flex justify-content-between align-items-center mt-2">
          <small className="text-muted">
            Showing {showingFrom}–{showingTo} of {allocations.length}
          </small>

          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button type="button" className="page-link " onClick={handlePrev}>
                  Previous
                </button>
              </li>
              <li className="page-item disabled">
                <span className="page-link">
                  Page {currentPage} of {totalPages}
                </span>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button type="button" className="page-link bg-secondary" onClick={handleNext}>
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {selectedKey && (
        <ViewKeyAllocatedDetail
          show={showDetailModal}
          onHide={handleCloseDetailModal}
          keyAllocation={selectedKey}
          statusColors={statusColors}
        />
      )}

      {handoverKey && (
        <Modal
          show={showHandoverModal}
          onHide={handleCloseHandoverModal}
          centered
          size="xl"
          autoFocus={false}
          enforceFocus={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Mark Key Status - ID: {handoverKey.id}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <div className="row g-3">
              <div className="col-md-6">
                <Form.Label className="fw-semibold">Mark Status</Form.Label>
                <Form.Select
                  value={markStatus}
                  onChange={(e) => setMarkStatus(e.target.value as 'RETURNED' | 'LOST')}
                  disabled={isProcessing}
                >
                  <option value="RETURNED">Returned</option>
                  <option value="LOST">Lost</option>
                </Form.Select>
              </div>
              {markStatus === 'RETURNED' && (
                <div className="col-md-6">
                  <Form.Label className="fw-semibold">Returned By</Form.Label>
                  <div className="dropdown">
                    <Form.Control
                      type="text"
                      placeholder={users.length === 0 ? 'No users available' : 'Search by name or emp ID'}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      disabled={isProcessing || users.length === 0}
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    />
                    <ul className="dropdown-menu w-100">
                      {filteredUsers.length === 0 ? (
                        <li>
                          <span className="dropdown-item text-muted">No matching users</span>
                        </li>
                      ) : (
                        filteredUsers.map((user) => (
                          <li key={user.empNumber}>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() => {
                                setSelectedUser(user.empNumber);
                                setSearchTerm(`${user.name} (${user.empNumber})`);
                              }}
                            >
                              {user.name} ({user.empNumber})
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleCloseHandoverModal} disabled={isProcessing}>
              Close
            </Button>
            <Button
              variant="success"
              onClick={handleMarkReturned}
              disabled={isProcessing || (markStatus === 'RETURNED' && !selectedUser)}
            >
              {isProcessing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="ti ti-check me-2" />
                  Mark {markStatus}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default KeyAllocationTable;
